import React from 'react';

/**
 * Top-level error fallback for the entire lab page.
 * Shows a full-page themed recovery UI when the whole app crashes.
 * 
 * Props:
 *   - error: The caught Error object
 *   - reset: Function to reset the error boundary and retry
 */
function LabErrorFallback({ error, reset }) {
  const isDev = process.env.NODE_ENV === 'development' ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost');

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '32px',
      textAlign: 'center',
      backgroundColor: 'var(--background, #1a1a1a)',
      color: 'var(--foreground, #e8e0d8)',
      fontFamily: 'var(--font-sans, "Libre Baskerville", serif)'
    }}>
      {/* Icon */}
      <div style={{
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        backgroundColor: 'var(--destructive, #dc2626)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '24px',
        fontSize: '28px',
        color: 'var(--destructive-foreground, #fff)'
      }}>
        !
      </div>

      <h1 style={{
        fontSize: '24px',
        fontWeight: 700,
        margin: '0 0 12px 0',
        color: 'var(--foreground, #e8e0d8)'
      }}>
        Something went wrong
      </h1>

      <p style={{
        fontSize: '16px',
        color: 'var(--muted-foreground, #999)',
        maxWidth: '480px',
        margin: '0 0 32px 0',
        lineHeight: 1.5
      }}>
        {isDev
          ? error.message
          : 'An unexpected error occurred in the lab. Your circuit data is safe — try recovering or reload the page.'
        }
      </p>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
        <button
          onClick={reset}
          style={{
            padding: '10px 24px',
            backgroundColor: 'var(--primary, #2563eb)',
            color: 'var(--primary-foreground, #fff)',
            border: 'none',
            borderRadius: 'var(--radius, 8px)',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: 600,
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => e.target.style.opacity = '0.85'}
          onMouseOut={(e) => e.target.style.opacity = '1'}
        >
          Try Again
        </button>

        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 24px',
            backgroundColor: 'var(--muted, #333)',
            color: 'var(--muted-foreground, #ccc)',
            border: '1px solid var(--border, #555)',
            borderRadius: 'var(--radius, 8px)',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: 500,
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => e.target.style.opacity = '0.85'}
          onMouseOut={(e) => e.target.style.opacity = '1'}
        >
          Reload Page
        </button>
      </div>

      {/* Dev-only error details */}
      {isDev && error.stack && (
        <details style={{
          marginTop: '32px',
          maxWidth: '700px',
          width: '100%',
          textAlign: 'left'
        }}>
          <summary style={{
            cursor: 'pointer',
            fontSize: '13px',
            color: 'var(--muted-foreground, #888)',
            marginBottom: '8px'
          }}>
            Stack trace (dev only)
          </summary>
          <pre style={{
            padding: '16px',
            backgroundColor: 'var(--card, #2a2a2a)',
            border: '1px solid var(--border, #444)',
            borderRadius: 'var(--radius, 8px)',
            color: 'var(--destructive, #ef4444)',
            fontSize: '12px',
            overflow: 'auto',
            maxHeight: '300px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {error.stack}
          </pre>
        </details>
      )}
    </div>
  );
}

export default LabErrorFallback;
